// src/pages/shop/ShopWholesaleSaleDetail.jsx
//
// Детали одной оптовой накладной:
// - заголовок с номером и датой
// - инфо о клиенте
// - таблица позиций
// - итого
// - заметка
// - кнопки: PDF (заглушка пока), Отменить

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { wholesaleSales as wsApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency } from '../../hooks/useMyShop';
import { useDeletePin } from '../../hooks/useDeletePin';
import { formatMoney } from '../../data/currencies';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { displayCode } from '../../utils/marking';
import { useShopNav } from './ShopHome';
import { generateInvoicePDF } from '../../utils/generateInvoicePDF';
import { shop as shopApi } from '../../api';

export default function ShopWholesaleSaleDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const navItems = useShopNav();
  const { show, ToastView } = useToast();
  const { confirm: confirmCancel, PinModal } = useDeletePin();

  // Фаза 2: как покупатель принял эту отгрузку (если он тоже на Alsatuu)
  const [acc, setAcc] = useState(null);
  const [resolving, setResolving] = useState(false);
  const currency = useMyCurrency();

  const [sale, setSale] = useState(null);
  const [myShop, setMyShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, shopData] = await Promise.all([
        wsApi.get(id),
        shopApi.my().catch(() => null),
      ]);
      setSale(data);
      setMyShop(shopData);
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  }, [id, show, t]);

  useEffect(() => { load(); }, [load]);

  // Фаза 2: как покупатель принял отгрузку (если он на Alsatuu и связь подтверждена)
  const loadAcceptance = useCallback(async () => {
    try {
      const r = await wsApi.acceptance(id);
      setAcc(r?.synced ? r : null);
    } catch (e) {
      setAcc(null);   // не синхронизировано — обычная накладная
    }
  }, [id]);

  useEffect(() => { loadAcceptance(); }, [loadAcceptance]);

  // Решение по недостаче: вернуть на склад или списать как бой.
  // В обоих случаях сумма накладной правится до факта → балансы сходятся у обоих.
  const resolveShortage = async (mode) => {
    setResolving(true);
    try {
      await wsApi.resolve(id, mode);
      show(t(mode === 'return' ? 'shortage.returned' : 'shortage.writtenOff'));
      await load();
      await loadAcceptance();
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    }
    setResolving(false);
  };

  const onCancel = async () => {
    const ok = await confirmCancel({
      title: t('wholesaleDetail.cancelTitle', 'Отменить накладную?'),
      message: t('wholesaleDetail.confirmCancel', { number: sale?.number || '' }),
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await wsApi.remove(id);
      show(t('wholesaleDetail.cancelled'));
      // Возвращаемся в карточку контрагента
      navigate(`/counterparty/${sale.counterpartyId}`, { replace: true });
    } catch (e) {
      show(e.message || t('common.error'), 'error');
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loader" style={{ padding: 64 }}><div className="spinner" /></div>
        <BottomNav items={navItems} />
      </div>
    );
  }
  if (!sale) {
    return (
      <div className="page">
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <Icon name="chevronLeft" size={18} />
          </button>
          <h1 className="page-title">{t('wholesaleDetail.notFound')}</h1>
        </div>
        <BottomNav items={navItems} />
      </div>
    );
  }

  const dateStr = new Date(sale.createdAt).toLocaleString();

  return (
    <div className="page" style={{ paddingBottom: 32 }}>
      {ToastView}
      {PinModal}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title">
          {t('wholesale.invoiceLabel', { number: sale.number })}
        </h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Инфо */}
        <div style={{
          padding: 14, marginBottom: 12,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-sm)',
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
            {t('wholesaleDetail.client')}
          </p>
          <p
            style={{ fontSize: 16, fontWeight: 700, cursor: 'pointer', color: 'var(--brand-primary)' }}
            onClick={() => navigate(`/counterparty/${sale.counterpartyId}`)}
          >
            {sale.counterparty?.name || '—'}
          </p>
          <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>
            {dateStr}
          </p>
        </div>

        {/* Позиции */}
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '8px 0' }}>
          {t('wholesaleDetail.itemsHeader')} ({sale.items.length})
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {sale.items.map((it, idx) => (
            <div
              key={it.id}
              style={{
                padding: 12,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              <p style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>
                {idx + 1}. {it.name}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--fg-muted)' }}>
                <span>{it.qty} {it.unit} × {formatMoney(it.salePrice, currency)}</span>
                <span style={{ fontWeight: 700, color: 'var(--fg)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(it.total, currency)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Итого */}
        <div style={{
          padding: 16, marginBottom: 12,
          background: 'var(--brand-primary-soft)',
          borderRadius: 'var(--r-md)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)' }}>
            {t('wholesale.total')}
          </p>
          <p style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-primary)', fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(sale.total, currency)}
          </p>
        </div>

        {/* Коды DataMatrix */}
        {Array.isArray(sale.codes) && sale.codes.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 8 }}>
              {t('wholesaleDetail.codesHeader')} ({sale.codes.length})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {sale.codes.map((c) => (
                <div key={c.seq} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-subtle)', borderRadius: 'var(--r-xs)' }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--fg-muted)', minWidth: 26 }}>№{c.seq}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name || '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--fg-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayCode(c.code)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Заметка */}
        {sale.note && (
          <div style={{
            padding: 12, marginBottom: 12,
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--r-sm)',
          }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {t('wholesale.note')}
            </p>
            <p style={{ fontSize: 13, lineHeight: 1.4 }}>{sale.note}</p>
          </div>
        )}

        {/* Кнопка PDF */}
        <button
          onClick={async () => {
            setPdfBusy(true);
            try {
              const result = await generateInvoicePDF({
                sale,
                counterparty: sale.counterparty,
                shop: myShop,
                currency,
              }, t);
              if (result.native) {
                show(t('reports.savedAndroid'));
              } else {
                show(t('reports.savedWeb'));
              }
            } catch (e) {
              console.error('[invoice-pdf] FAILED:', e);
              show('PDF error: ' + (e?.message || String(e)), 'error');
            } finally {
              setPdfBusy(false);
            }
          }}
          disabled={pdfBusy}
          className="btn btn-secondary"
          style={{ width: '100%', marginBottom: 8 }}
        >
          <Icon name="copy" size={14} />
          <span style={{ marginLeft: 4 }}>
            {pdfBusy ? t('common.saving') : t('wholesaleDetail.downloadPdf')}
          </span>
        </button>

        {/* Кнопка отмены */}
        <button
          onClick={onCancel}
          disabled={cancelling}
          className="btn btn-ghost"
          style={{ width: '100%', color: 'var(--danger)' }}
        >
          <Icon name="trash" size={14} />
          <span style={{ marginLeft: 4 }}>{cancelling ? t('common.saving') : t('wholesaleDetail.cancelInvoice')}</span>
        </button>

        <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 8, textAlign: 'center', lineHeight: 1.4 }}>
          {t('wholesaleDetail.cancelHint')}
        </p>

        <div style={{ height: 40 }} />
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}
